import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../lib/api';

interface Snapshot {
  snapshotId: string;
  name: string;
  description?: string;
  firstLine: string;
  createdAt: string;
  chapterCount: number;
  chapterTitles: string[];
}

interface VersionHistoryPanelProps {
  restUrl: string;
  onRestoreComplete: () => void;
}

export function VersionHistoryPanel({ restUrl, onRestoreComplete }: VersionHistoryPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchWithAuth(`${restUrl}snapshots`)
      .then(r => r.json())
      .then((data: { snapshots: Snapshot[] }) => setSnapshots(data.snapshots ?? []))
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
  }, [isOpen, restUrl]);

  async function handleSaveSnapshot() {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const r = await fetchWithAuth(`${restUrl}snapshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: saveName.trim(), description: saveDesc.trim() || undefined }),
      });
      const data = await r.json() as { snapshotId: string; name: string; createdAt: string };
      const newSnap: Snapshot = {
        snapshotId: data.snapshotId,
        name: data.name,
        createdAt: data.createdAt,
        firstLine: '',
        chapterCount: 0,
        chapterTitles: [],
      };
      setSnapshots(prev => [newSnap, ...prev]);
      setSaveName('');
      setSaveDesc('');
      setSaveOpen(false);
      showToast(`Snapshot saved: ${data.name}`);
    } catch {
      showToast('Failed to save snapshot — please try again');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore(snapshotId: string) {
    setRestoring(snapshotId);
    try {
      await fetchWithAuth(`${restUrl}snapshots/${snapshotId}/restore`, { method: 'POST' });
      showToast('Book restored — reloading...');
      setTimeout(() => onRestoreComplete(), 1200);
    } catch {
      showToast('Failed to restore snapshot');
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  }

  return (
    <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
      {/* Toggle header — matches BookSettings pattern */}
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors"
      >
        <span>Version History</span>
        <span className="text-stone-400">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-3 border-t border-stone-100">
          {/* Save Snapshot */}
          <div className="pt-3">
            {!saveOpen ? (
              <button
                type="button"
                onClick={() => setSaveOpen(true)}
                className="text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors"
              >
                + Save Snapshot
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  placeholder="Snapshot name (required)"
                  className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                  autoFocus
                />
                <input
                  type="text"
                  value={saveDesc}
                  onChange={e => setSaveDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSaveSnapshot}
                    disabled={saving || !saveName.trim()}
                    className="px-3 py-1.5 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSaveOpen(false); setSaveName(''); setSaveDesc(''); }}
                    className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Snapshot list */}
          {loading && <p className="text-sm text-stone-400">Loading snapshots...</p>}
          {!loading && snapshots.length === 0 && (
            <p className="text-sm text-stone-400">No snapshots yet — save one before a major rewrite.</p>
          )}
          {snapshots.map(snap => (
            <div key={snap.snapshotId} className="border border-stone-200 rounded-lg p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-stone-800 truncate">{snap.name}</p>
                  <p className="text-xs text-stone-400">
                    {new Date(snap.createdAt).toLocaleDateString()} · {snap.chapterCount} chapter{snap.chapterCount !== 1 ? 's' : ''}
                  </p>
                  {snap.firstLine && (
                    <p className="text-xs text-stone-500 mt-1 line-clamp-2">{snap.firstLine}</p>
                  )}
                </div>
                {confirmRestore === snap.snapshotId ? (
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRestore(snap.snapshotId)}
                      disabled={restoring === snap.snapshotId}
                      className="px-2 py-1 text-xs font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {restoring === snap.snapshotId ? '...' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRestore(null)}
                      className="px-2 py-1 text-xs text-stone-500 hover:text-stone-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmRestore(snap.snapshotId)}
                    className="shrink-0 px-2 py-1 text-xs font-semibold border border-stone-300 text-stone-600 rounded hover:bg-stone-50"
                  >
                    Restore
                  </button>
                )}
              </div>
              {confirmRestore === snap.snapshotId && (
                <p className="text-xs text-stone-500">
                  Restore &quot;{snap.name}&quot;? Your current book will be replaced. Locked chapters will stay locked.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
